/**
 * useFieldQueue (5.4 A-Phase 2 — the liveQuery reactivity fix) — THE one
 * live subscription to the field syncQueue. Every live consumer (chip
 * counts via useFieldDrain, sheet buckets, future Path-2 actions) derives
 * from this hook, so observability can only break — and be probed — in ONE
 * place.
 *
 * ZONE CONTRACT (the bug this fixes): useLiveQuery registers dependencies
 * only for reads that execute inside Dexie's tracking zone (PSD), and that
 * zone does not reliably survive a foreign await in the transpiled bundle.
 * The old queriers hopped through `await ensureOpen()` (a native-promise
 * hop inside async, try/catch-wrapped getQueue) BEFORE the toArray() read —
 * the read registered ZERO dependencies, the subscription never re-fired,
 * and both consumers served mount-time snapshots forever. The querier here
 * therefore starts the Dexie read SYNCHRONOUSLY: the table read directly
 * when the DB is open, else fieldDb.open() — a DexiePromise, whose .then
 * stays inside the tracked zone — chained to the read (the post-purge
 * closed-DB edge that ensureOpen existed for; Dexie 4 never auto-reopens
 * after delete(), fieldStore 4.2a gotcha). NEVER put an await, async
 * wrapper, or try/catch hop before the read starts. No error handling in
 * the querier by design: a rejected read surfaces through the hook exactly
 * as the old getQueue rethrow did, and wrapping it would re-introduce the
 * zone-breaking microtask hop.
 *
 * Returns the raw items array, FIFO by time-prefixed outbox_id pk
 * (orderBy(':id') — identical order to fieldOutbox.getQueue, which remains
 * the one-shot imperative read for non-live callers). Default [] while the
 * first query is in flight. Pure passthrough — no mapping/caching between
 * Dexie and the subscriber.
 */

import { useLiveQuery } from 'dexie-react-hooks';
import { fieldDb } from '../db/fieldDb';
import { markCaptureResult } from './fieldOutbox';
import './rebaseRecord'; // side effect: registers window.__fieldRebaseProbe (Path 2 Phase 2)

const readQueue = () => fieldDb.syncQueue.orderBy(':id').toArray();

export function useFieldQueue() {
  return useLiveQuery(
    () => (fieldDb.isOpen() ? readQueue() : fieldDb.open().then(readQueue)),
    [],
    [],
  );
}

/**
 * Reactivity probe (permanent verification artifact — the test that was
 * structurally missing when the frozen-subscription bug shipped). From the
 * DevTools console on any /field page:
 *
 *   __fieldQueueProbe()                     — console.table the live queue
 *   __fieldQueueProbe('<outbox_id>', 'error')   — flip an item's status via
 *   __fieldQueueProbe('<outbox_id>', 'pending') — the REAL production write
 *                                                 path (markCaptureResult)
 *
 * Acceptance: with the sheet OPEN (no remount, no reload), the flipped item
 * re-buckets and the chip re-counts the moment the promise resolves. Flip
 * back to restore state. Inert unless called; console access already
 * implies IDB access, so this exposes nothing new.
 */
if (typeof window !== 'undefined') {
  window.__fieldQueueProbe = async (outboxId, status) => {
    if (!outboxId) {
      const items = await readQueue();
      console.table(items.map((i) => ({
        outbox_id: i.outbox_id, type: i.type, status: i.status, error: i.error ?? '',
      })));
      return `${items.length} item(s) — __fieldQueueProbe(outbox_id, status) to flip one`;
    }
    await markCaptureResult(outboxId, status || 'error');
    return `flipped ${outboxId} → ${status || 'error'} — sheet/chip must re-bucket WITHOUT remount`;
  };
}
